/**
 * FolderNameModal — Shared presentational modal for "New Folder" and
 * "Rename Folder".
 *
 * Dumb/presentational: owns no mutation and no business state. The parent
 * controls `value`, submission, disabled state, and toast handling.
 * Enter on the input triggers `onSubmit` when not disabled.
 */

import { useCallback } from "react";
import Modal from "../../../../../../../../../components/Modal/Modal";
import Button from "../../../../../../../../../components/Button/Button";
import OpenFolderIcon from "../../../../../../../../../assets/svg/open-folder-icon";
import type { FolderNameModalProps } from "../../types/fileManagement.types";

const FolderNameModal = ({
  open,
  title,
  value,
  onChange,
  onSubmit,
  onCancel,
  isSubmitting,
  isSubmitDisabled,
  submitLabel,
  cancelLabel = "Cancel",
  labelText = "Folder name",
  placeholder = "e.g, Brand Assets",
}: FolderNameModalProps) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !isSubmitDisabled) {
        onSubmit();
      }
    },
    [onSubmit, isSubmitDisabled]
  );

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      preventOutsideClick={false}
      customHeader={
        <div className="flex items-center gap-1.5 flex-1">
          <OpenFolderIcon className="w-6 h-6 text-analytics-blue dark:text-analytics-blue-dk" />
          <span className="text-heading-text dark:text-heading-text-dk font-medium text-lg">
            {title}
          </span>
        </div>
      }
      headerClassName="px-6 pt-[18px] pb-0"
      contentClassName="w-[380px] border border-border-gray dark:border-border-gray-dk bg-fixed-white dark:bg-popper-bg-dk rounded-lg shadow-[10px_10px_60px_0px_rgba(0,0,0,0.2)]"
      overlayClassName="bg-black/80"
      bodyClassName="!p-0"
    >
      <div
        className="flex flex-col px-6 pb-[18px]"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="flex flex-col gap-1.5 mt-9">
          <label className="text-md font-normal text-heading-text dark:text-heading-text-dk">
            {labelText}
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoFocus
            className="w-[264px] px-2 py-2 text-md font-normal text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg outline-none focus:border-verba-blue dark:focus:border-verba-blue-dk transition-colors"
          />
        </div>

        <div className="flex items-center justify-end gap-3 mt-[36px]">
          <Button
            innerText={cancelLabel}
            onClick={onCancel}
            disabled={isSubmitting}
            buttonContainerClass="min-w-[100px] min-h-[26px] px-2 py-1 border border-border-gray dark:border-border-gray-dk rounded-md cursor-pointer hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
            innerTextClass="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk"
          />
          <Button
            innerText={submitLabel}
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            buttonContainerClass="px-6 py-1 min-h-[26px] bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk rounded-md cursor-pointer hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            innerTextClass="text-md font-semibold text-pure-white dark:text-pure-white-dk"
          />
        </div>
      </div>
    </Modal>
  );
};

export default FolderNameModal;
